import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useChartAccessibility } from '../../hooks/useChartAccessibility';
import { ChartPatternDefs } from '../accessibility/ChartPatternDefs';
import { AccessibleDataTable } from '../accessibility/AccessibleDataTable';

export interface BarChartDataPoint {
  name: string;
  [key: string]: string | number;
}

export interface BarConfig {
  dataKey: string;
  name?: string;
  color?: string;
  stackId?: string;
  radius?: number;
}

export interface BarChartProps {
  data: BarChartDataPoint[];
  bars: BarConfig[];
  height?: number | string;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  xAxisDataKey?: string;
  yAxisLabel?: string;
  layout?: 'horizontal' | 'vertical';
  colorByValue?: boolean;
  colors?: string[];
  className?: string;
  /** Called when a bar is clicked. Receives the underlying data row. */
  onBarClick?: (row: BarChartDataPoint) => void;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
];

export function BarChart({
  data,
  bars,
  height = '100%',
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  xAxisDataKey = 'name',
  yAxisLabel,
  layout = 'horizontal',
  colorByValue = false,
  colors = DEFAULT_COLORS,
  className = '',
  onBarClick,
}: BarChartProps) {
  const isVertical = layout === 'vertical';
  const a11y = useChartAccessibility();
  const usePatterns = a11y.usePatternFills;
  // Per-series color (multi-series) and per-cell color (colorByValue).
  const barColorAt = (index: number) =>
    a11y.resolveColor(index, bars[index]?.color || colors[index % colors.length]);
  const cellColorAt = (index: number) =>
    a11y.resolveColor(index, (data[index] as any)?.color || colors[index % colors.length]);
  const patternColors = colorByValue
    ? data.map((_, i) => cellColorAt(i))
    : bars.map((_, i) => barColorAt(i));

  const clickHandler = onBarClick
    ? (payload: any) => {
        const row = payload && payload.payload ? payload.payload : payload;
        if (row) onBarClick(row as BarChartDataPoint);
      }
    : undefined;

  const tableColumns = [
    { key: xAxisDataKey, label: 'Name' },
    ...bars.map((b) => ({ key: b.dataKey, label: b.name || b.dataKey })),
  ];

  return (
    <div className={`w-full h-full flex flex-col ${className}`} style={typeof height === 'number' ? { height } : undefined}>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={data}
            layout={layout}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            {usePatterns && ChartPatternDefs({ colors: patternColors })}
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={a11y.gridColor} />}
            {isVertical ? (
              <>
                <XAxis type="number" tick={{ fontSize: 12 }} stroke={a11y.axisColor} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey={xAxisDataKey}
                  tick={{ fontSize: 12 }}
                  stroke={a11y.axisColor}
                  width={100}
                />
              </>
            ) : (
              <>
                <XAxis
                  dataKey={xAxisDataKey}
                  tick={{ fontSize: 12 }}
                  stroke={a11y.axisColor}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke={a11y.axisColor}
                  label={
                    yAxisLabel
                      ? { value: yAxisLabel, angle: -90, position: 'insideLeft' }
                      : undefined
                  }
                />
              </>
            )}
            {showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: a11y.tooltipBg,
                  border: `1px solid ${a11y.tooltipBorder}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  color: a11y.tooltipText,
                }}
                labelStyle={{ color: a11y.tooltipText }}
                itemStyle={{ color: a11y.tooltipText }}
              />
            )}
            {showLegend && <Legend />}
            {bars.map((bar, index) => (
              <Bar
                key={bar.dataKey}
                dataKey={bar.dataKey}
                name={bar.name || bar.dataKey}
                fill={usePatterns ? `url(#pattern-${index})` : barColorAt(index)}
                stackId={bar.stackId}
                radius={bar.radius || 4}
                onClick={clickHandler as any}
                style={clickHandler ? { cursor: 'pointer' } : undefined}
                isAnimationActive={a11y.isAnimationActive}
                animationDuration={a11y.animationDuration}
              >
                {colorByValue &&
                  data.map((_, cellIndex) => (
                    <Cell
                      key={`cell-${cellIndex}`}
                      fill={usePatterns ? `url(#pattern-${cellIndex})` : cellColorAt(cellIndex)}
                    />
                  ))}
              </Bar>
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      <AccessibleDataTable caption="Bar chart data" columns={tableColumns} data={data} />
    </div>
  );
}
